// Settings → Fleet and CI (P-004), owners only: the GitHub App's status, the hosts that report to
// the board (add with a token shown once, new token, remove) and the runners (where each runs, the
// restart switch); the alerts are AlertsSettings.tsx. The App's keys stay in board.env: this page
// shows status only.
import { useState, type FormEvent } from 'react';
import { api, errorMessage, type Fleet, type FleetGitHub, type FleetHostToken } from '../api/client';
import { KIND_LABEL, RunnerBadge } from '../components/Fleet';
import { Icon } from '../components/Icon';
import { Badge, Button, Callout, Input, Switch, Table, Td, Th, cn, mono } from '../components/ui';
import { ago } from '../lib/time';
import { useLoad, type Loaded } from '../lib/useLoad';

export type Outcome = { ok: boolean; text: string } | null;

export const H2 = 'm-0 mb-1 text-[14px] font-semibold';
export const H3 = 'm-0 mb-1 mt-5 text-[13px] font-semibold';
export const SUB = 'm-0 mb-3.5 text-[12.5px] text-ink-muted';
export const SECTION = 'min-w-0 scroll-mt-[72px]';
export const SMALL = 'block text-[11.5px] text-ink-muted';
export const KV = 'grid grid-cols-[140px_minmax(0,1fr)] gap-x-4 gap-y-2.5 border-y border-hairline py-3 text-[12.5px] phone:grid-cols-[minmax(0,1fr)] phone:gap-y-1';
export const K = 'text-ink-muted phone:mt-1.5';

export function Status({ outcome }: { outcome: Outcome }) {
  if (!outcome) return null;
  return (
    <p
      className={cn('mb-0 mt-2.5 flex items-center gap-1.5 text-[12.5px]', outcome.ok ? 'text-checks-pass-ink' : 'font-medium text-checks-fail-ink')}
      role={outcome.ok ? 'status' : 'alert'}
    >
      {!outcome.ok && <Icon name="warn" small />}
      {outcome.text}
    </p>
  );
}

/** A value to copy: shown in full, with a Copy button (and selectable where the clipboard is not). */
export function CopyField({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      <code className={cn(mono, 'min-w-0 select-all rounded border border-hairline bg-surface px-2 py-1 text-[12px] text-ink-display [overflow-wrap:anywhere]')}>
        {value}
      </code>
      <Button
        size="sm"
        aria-label={`Copy ${label}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        <Icon name="copy" small />
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </span>
  );
}

// ------------------------------------------------------------------ GitHub App

export function GitHubAppSection({ ws }: { ws: number }) {
  const [gh, reload] = useLoad(() => api.GET('/api/workspaces/{ws_id}/fleet/github', { params: { path: { ws_id: ws } } }), `${ws}`);
  return (
    <section id="github" aria-labelledby="h-github" className={SECTION}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 id="h-github" className={H2}>
          GitHub App
        </h2>
        {gh.state === 'ok' && <AppBadge gh={gh.data} />}
      </div>
      <p className={SUB}>
        How this board reads repos, pull requests, checks and runners. The App ID and private key live on the server in{' '}
        <span className={mono}>board.env</span>; they are never shown or changed here.
      </p>
      {gh.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {gh.state === 'error' && <Status outcome={{ ok: false, text: gh.message }} />}
      {gh.state === 'ok' && <GitHubApp gh={gh.data} onCheck={reload} />}
    </section>
  );
}

function AppBadge({ gh }: { gh: FleetGitHub }) {
  const missing = gh.permissions.filter((p) => !p.granted).length + gh.events.filter((e) => !e.granted).length;
  if (gh.installations.length === 0) return <Badge tone="warning">Not installed on this workspace's repos</Badge>;
  return missing ? <Badge tone="warning">{missing} missing</Badge> : <Badge tone="success">Connected</Badge>;
}

function Check({ ok, label, why, level }: { ok: boolean; label: string; why: string; level?: string }) {
  return (
    <li className="flex min-w-0 items-start gap-2.5 border-t border-hairline py-2.5 first:border-t-0">
      <span className={cn('mt-0.5 flex-none', ok ? 'text-success-ink' : 'text-warning-ink')}>
        <Icon name={ok ? 'check' : 'warn'} label={ok ? 'Granted' : 'Missing'} />
      </span>
      <span className="min-w-0 flex-1">
        <b className="font-medium text-ink-display">
          {label}
          {level ? `: ${level === 'write' ? 'read and write' : 'read'}` : ''}
        </b>
        <span className={SMALL}>{why}</span>
      </span>
      {ok ? <span className="text-[11.5px] text-ink-muted">Granted</span> : <Badge tone="warning">Missing</Badge>}
    </li>
  );
}

function GitHubApp({ gh, onCheck }: { gh: FleetGitHub; onCheck: () => void }) {
  const missingSources = gh.sources.filter((s) => s.missing_permission);
  return (
    <>
      <div className={KV}>
        <span className={K}>App</span>
        <span>
          {gh.app?.html_url ? (
            <a href={gh.app.html_url} target="_blank" rel="noopener noreferrer" className={mono}>
              {gh.app.slug ?? gh.app.name}
            </a>
          ) : (
            <span className="text-ink-muted">not read yet (the worker reads it within a few minutes)</span>
          )}
        </span>
        <span className={K}>Installed on</span>
        <span className="grid gap-1.5">
          {gh.installations.length === 0 && <span className="text-ink-muted">none of this workspace's repos</span>}
          {gh.installations.map((i) => (
            <span key={i.account}>
              <b className="font-semibold text-ink-display">{i.account}</b>{' '}
              <span className="text-ink-muted">
                ({i.account_type === 'Organization' ? 'organisation' : 'account'}
                {i.repository_selection === 'all' ? ', all repositories' : ''})
              </span>
              <span className={SMALL}>{i.repos.join(', ')}</span>
            </span>
          ))}
        </span>
        <span className={K}>Webhook</span>
        <span className="grid gap-1.5">
          <CopyField value={gh.webhook_url} label="the webhook URL" />
          <span className={SMALL}>
            {gh.last_delivery ? `Last delivery ${ago(gh.last_delivery.at)} (${gh.last_delivery.event})` : 'No delivery yet'}
          </span>
        </span>
      </div>
      <div className="mt-5 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className={cn(H3, 'mt-0')}>Permissions and events</h3>
          <p className={SUB}>What the App may read and which events it sends. Add missing ones on GitHub, then accept them on each installation.</p>
        </div>
        <span className="flex gap-1.5">
          {gh.app?.html_url && (
            <a className={cn('inline-flex h-[30px] items-center gap-1.5 rounded border border-hairline px-2.5 text-[12.5px] font-medium text-ink-display hover:no-underline')} href={gh.app.html_url} target="_blank" rel="noopener noreferrer">
              Review on GitHub <Icon name="ext" small />
            </a>
          )}
          <Button onClick={onCheck}>Check again</Button>
        </span>
      </div>
      <ul className="m-0 list-none rounded-lg border border-hairline px-3.5 py-0" aria-label="Permissions">
        {gh.permissions.map((p) => (
          <Check key={p.key} ok={p.granted} label={p.label} level={p.level} why={p.why} />
        ))}
        {gh.events.map((e) => (
          <Check key={e.key} ok={e.granted} label={`${e.label} event`} why={e.why} />
        ))}
      </ul>
      {missingSources.length > 0 && (
        <Callout tone="warning" className="mt-3" role="note" icon={<Icon name="warn" />}>
          GitHub refused to list runners for {missingSources.map((s) => `${s.source} (needs ${s.missing_permission})`).join(', ')}: their
          runners stay empty until the permission is granted and accepted.
        </Callout>
      )}
    </>
  );
}

// ------------------------------------------------------------------ Hosts

function TokenBox({ issued, onDone }: { issued: FleetHostToken; onDone: () => void }) {
  return (
    <div className="mt-3 grid gap-2.5 rounded-lg border border-hairline-strong bg-surface p-3.5" role="status" aria-label={`Token for ${issued.host.name}`}>
      <b className="font-semibold text-ink-display">Token for {issued.host.name}. Shown once.</b>
      <CopyField value={issued.token} label="the token" />
      <span className="text-[12.5px] text-ink-body">On {issued.host.name}, from a Shipyard checkout, run:</span>
      <CopyField value={issued.install_command} label="the install command" />
      <span className={SMALL}>
        It creates the host-reporter user, lists the runner services it finds for restart, and starts reporting. The host shows
        Online on the Fleet page within a few seconds. On a Mac, add <span className={mono}>--launchd</span> and run it without sudo.
      </span>
      <div>
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}

export function HostsSection({ ws, fleet, reload }: { ws: number; fleet: Loaded<Fleet>; reload: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<FleetHostToken | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts', {
      params: { path: { ws_id: ws } },
      body: { name: name.trim().toLowerCase() },
    });
    setBusy(false);
    if (data) {
      setName('');
      setIssued(data);
      reload();
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  async function newToken(id: number) {
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/token', {
      params: { path: { ws_id: ws, host_id: id } },
    });
    if (data) setIssued(data);
    else setOutcome({ ok: false, text: errorMessage(error, response.status) });
    reload();
  }
  async function remove(id: number, host: string) {
    const { error, response } = await api.DELETE('/api/workspaces/{ws_id}/fleet/hosts/{host_id}', {
      params: { path: { ws_id: ws, host_id: id } },
    });
    setRemoving(null);
    setOutcome(response.ok ? { ok: true, text: `Removed ${host}; its token no longer works.` } : { ok: false, text: errorMessage(error, response.status) });
    if (issued?.host.id === id) setIssued(null);
    reload();
  }
  return (
    <section id="hosts" aria-labelledby="h-hosts" className={SECTION}>
      <h2 id="h-hosts" className={H2}>
        Hosts
      </h2>
      <p className={SUB}>
        Machines that report to this board. Each has its own token; removing a host or issuing a new token cuts the old one off at
        once. A host sends state only, never what agents write.
      </p>
      {fleet.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {fleet.state === 'error' && <Status outcome={{ ok: false, text: fleet.message }} />}
      {fleet.state === 'ok' && fleet.data.hosts.length === 0 && <p className="text-ink-muted">No hosts yet.</p>}
      {fleet.state === 'ok' && fleet.data.hosts.length > 0 && (
        <Table aria-label="Hosts">
          <thead>
            <tr>
              <Th>Machine</Th>
              <Th>Reporter</Th>
              <Th className="text-right">Last seen</Th>
              <Th>Slot limit</Th>
              <Th>Token</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {fleet.data.hosts.map((h) => (
              <tr key={h.id} data-host={h.name}>
                <Td>
                  <b className={cn(mono, 'font-semibold')}>{h.name}</b>
                  <small className={SMALL}>
                    {KIND_LABEL[h.kind]}
                    {h.os ? ` · ${h.os === 'macos' ? 'macOS' : h.os === 'linux' ? 'Linux' : h.os}` : ''}
                  </small>
                </Td>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className={mono}>{h.reporter ?? '—'}</span>
                    {h.update_available && <Badge tone="warning">Update available</Badge>}
                  </span>
                </Td>
                <Td className="whitespace-nowrap text-right text-ink-muted">
                  {h.online ? 'now' : h.last_seen_at ? ago(h.last_seen_at) : 'never'}
                </Td>
                <Td className="text-ink-muted">{h.slot_limit ?? '—'}</Td>
                <Td className={cn(mono, 'text-ink-muted')}>…{h.token_hint}</Td>
                <Td className="text-right">
                  <span className="inline-flex flex-wrap justify-end gap-1.5">
                    {removing === h.id ? (
                      <>
                        <Button variant="danger" size="sm" onClick={() => void remove(h.id, h.name)}>
                          Remove {h.name}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRemoving(null)}>
                          Keep
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => void newToken(h.id)} aria-label={`New token for ${h.name}`}>
                          New token
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRemoving(h.id)} aria-label={`Remove ${h.name}`}>
                          Remove
                        </Button>
                      </>
                    )}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {issued ? (
        <TokenBox issued={issued} onDone={() => setIssued(null)} />
      ) : (
        <form className="mt-3 flex flex-wrap gap-2" onSubmit={add}>
          <label className="sr-only" htmlFor="add-host">
            Machine name
          </label>
          <Input
            id="add-host"
            className="min-w-[180px] max-w-[300px] flex-1 phone:min-w-0 phone:max-w-none phone:basis-full"
            placeholder="Machine name, e.g. orca-vps"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            <Icon name="plus" />
            Add a host
          </Button>
        </form>
      )}
      <Status outcome={outcome} />
    </section>
  );
}

// ------------------------------------------------------------------ Runners

export function RunnersSection({ ws, fleet, reload }: { ws: number; fleet: Loaded<Fleet>; reload: () => void }) {
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [gh] = useLoad(() => api.GET('/api/workspaces/{ws_id}/fleet/github', { params: { path: { ws_id: ws } } }), `${ws}`);
  async function setSwitch(runner: string, enabled: boolean) {
    const { data, error, response } = await api.PUT('/api/workspaces/{ws_id}/fleet/runners/{runner}/restart-switch', {
      params: { path: { ws_id: ws, runner } },
      body: { enabled },
    });
    setOutcome(
      data
        ? { ok: true, text: `Restart of ${runner} ${enabled ? 'allowed' : 'switched off'}.` }
        : { ok: false, text: errorMessage(error, response.status) },
    );
    reload();
  }
  return (
    <section id="runners" aria-labelledby="h-runners" className={SECTION}>
      <h2 id="h-runners" className={H2}>
        Runners
      </h2>
      <p className={SUB}>
        Every self-hosted runner GitHub reports for this workspace's repos, and the machine it runs on. GitHub decides online and busy;
        the machine's reporter adds its service state and does restarts.
      </p>
      {fleet.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {fleet.state === 'error' && <Status outcome={{ ok: false, text: fleet.message }} />}
      {fleet.state === 'ok' && fleet.data.runners.length === 0 && (
        <p className="text-ink-muted">No runners: GitHub lists none for these repos, or the App may not list them (see GitHub App).</p>
      )}
      {fleet.state === 'ok' && fleet.data.runners.length > 0 && (
        <Table aria-label="Runners">
          <thead>
            <tr>
              <Th>Runner</Th>
              <Th>Registered on</Th>
              <Th>GitHub</Th>
              <Th>Machine</Th>
              <Th>Restart from the board</Th>
            </tr>
          </thead>
          <tbody>
            {fleet.data.runners.map((r) => (
              <tr key={r.name} data-runner={r.name}>
                <Td>
                  <b className={cn(mono, 'font-semibold')}>{r.name}</b>
                  {r.github && r.github.labels.length > 0 && <small className={SMALL}>{r.github.labels.join(', ')}</small>}
                </Td>
                <Td className="text-ink-muted">{r.github ? r.github.source : '—'}</Td>
                <Td>
                  <RunnerBadge runner={r} />
                </Td>
                <Td>
                  {r.host ? (
                    <>
                      <span className={mono}>{r.host.name}</span>
                      <small className={SMALL}>matched by its reporter</small>
                    </>
                  ) : (
                    <>
                      <Badge>Not matched</Badge>
                      <small className={SMALL}>install the reporter on its machine</small>
                    </>
                  )}
                </Td>
                <Td>
                  {r.listed ? (
                    <Switch
                      checked={r.restart_enabled}
                      onChange={(v) => void setSwitch(r.name, v)}
                      aria-label={`Allow restart of ${r.name}`}
                    />
                  ) : (
                    <span className="text-[12px] text-ink-muted">{r.host ? 'Not on its machine’s list' : 'Not available'}</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Status outcome={outcome} />
      <Callout tone="info" className="mt-3" icon={<Icon name="eye" />}>
        A machine only restarts runner services on its own list, written by its install script. Switching one off here hides its Restart
        button; it cannot add a runner the machine has not listed.
      </Callout>
      <h3 className={H3}>Where runners are read from</h3>
      <p className={SUB}>The repos this workspace tracks, and the organisations the App is installed on that own them.</p>
      {gh.state === 'ok' && (
        <div className={KV}>
          {gh.data.sources.length === 0 && <span className="text-ink-muted">No repos yet.</span>}
          {gh.data.sources.map((s) => (
            <span key={`${s.kind}:${s.source}`} className="contents">
              <span className={cn(K, mono)}>{s.source}</span>
              <span>
                {s.kind === 'org' ? 'organisation' : 'repo'} ·{' '}
                {s.missing_permission ? (
                  <Badge tone="warning">needs {s.missing_permission}</Badge>
                ) : s.status === null ? (
                  <span className="text-ink-muted">not read yet</span>
                ) : s.error ? (
                  <span className="text-warning-ink">{s.error}</span>
                ) : (
                  <span className="text-ink-muted">
                    {s.runners === 0 ? 'no self-hosted runners' : `${s.runners} runner${s.runners === 1 ? '' : 's'}`}
                  </span>
                )}
              </span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/** The fleet, loaded once for Settings → Hosts and Runners. */
export function useFleet(ws: number) {
  return useLoad<Fleet>(() => api.GET('/api/workspaces/{ws_id}/fleet', { params: { path: { ws_id: ws } } }), `${ws}`);
}
